import React, { useEffect, useMemo, useState } from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Loader2, X } from 'lucide-react';
import { FormField } from '../../../components/app';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { AccrualDialogProps } from '../types';
import { usersApi, User } from '../../../api/users';

/** Покупателя ищем по нику, email или id — id приходит из RevenueCat. */
function useUserSearch(query: string) {
  const [results, setResults] = useState<User[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 3) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        // Похожее на UUID ищем по id, остальное — обычным поиском.
        const isId = /^[0-9a-fA-F-]{6,}$/.test(term) && term.includes('-');
        const response = await usersApi.getUsers({
          ...(isId ? { id: term } : { search: term }),
          limit: 8,
          isDeleted: 'all',
        });
        if (!cancelled) setResults(response.users);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  return { results, isSearching };
}

export function AccrualDialog({
  isOpen,
  onOpenChange,
  providers,
  formData,
  onFormDataChange,
  onSubmit,
  isEditing,
  isSubmitting,
  formatMoney,
}: AccrualDialogProps) {
  const [userQuery, setUserQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { results, isSearching } = useUserSearch(userQuery);

  const provider = useMemo(
    () => providers.find((p) => p.id === formData.providerId),
    [providers, formData.providerId],
  );
  const currency = provider?.currency || 'UZS';
  const needsRate = currency !== 'UZS';
  const rate = needsRate ? formData.rate || 0 : 1;

  // Пересчёт в шапке формы: сколько это в сумах и что останется после комиссии.
  const amountUzs = (formData.amount || 0) * rate;
  const net = (formData.amount || 0) * (1 - (provider?.commission_percent || 0) / 100);

  useEffect(() => {
    if (!isOpen) {
      setUserQuery('');
      setSelectedUser(null);
      return;
    }
    // При редактировании в форме лежит только id — подтягиваем ник, иначе в
    // поле покупателя висел бы голый uuid.
    if (!formData.userId || selectedUser?.id === formData.userId) return;
    let cancelled = false;
    usersApi
      .getUsers({ id: formData.userId, limit: 1, isDeleted: 'all' })
      .then((response) => {
        if (!cancelled) setSelectedUser(response.users[0] || null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isOpen, formData.userId]);

  const pickUser = (user: User) => {
    setSelectedUser(user);
    setUserQuery('');
    onFormDataChange((prev) => ({ ...prev, userId: user.id }));
  };

  const clearUser = () => {
    setSelectedUser(null);
    setUserQuery('');
    onFormDataChange((prev) => ({ ...prev, userId: null }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-0">
        <DialogHeader bordered>
          <DialogTitle>{isEditing ? 'Изменить начисление' : 'Добавить начисление'}</DialogTitle>
          <DialogDescription>
            Одна продажа у провайдера, чьих платежей нет в базе. Удержанный налог или возврат покупателю
            заводится отрицательной суммой. Премиум пользователю эта запись не выдаёт.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Дата продажи" required>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => onFormDataChange((prev) => ({ ...prev, date: e.target.value }))}
              />
            </FormField>
            <FormField label="Провайдер" required>
              {(p) => (
                <Select
                  value={formData.providerId}
                  onValueChange={(value) => onFormDataChange((prev) => ({ ...prev, providerId: value }))}
                >
                  <SelectTrigger {...p}>
                    <SelectValue placeholder="Выберите провайдера" />
                  </SelectTrigger>
                  <SelectContent>
                    {providers.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </FormField>
          </div>

          <div className={cn('grid gap-4', needsRate && 'sm:grid-cols-2')}>
            <FormField label={`Сумма, ${currency}`} required hint="Отрицательная — корректировка">
              <Input
                type="number"
                step="0.01"
                inputMode="decimal"
                value={formData.amount || ''}
                onChange={(e) => onFormDataChange((prev) => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
              />
            </FormField>
            {needsRate && (
              <FormField label={`Курс ${currency} → сум`} required hint="Сумов за 1 единицу, после сохранения не пересчитывается">
                <Input
                  type="number"
                  step="0.0001"
                  inputMode="decimal"
                  value={formData.rate || ''}
                  onChange={(e) => onFormDataChange((prev) => ({ ...prev, rate: parseFloat(e.target.value) || 0 }))}
                />
              </FormField>
            )}
          </div>

          {!!formData.amount && (
            <dl className="m-0 flex flex-col gap-1 rounded-xl border border-border-soft bg-surface-alt px-4 py-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-text-2">Чистое (−{provider?.commission_percent || 0}%)</dt>
                <dd className="m-0 font-semibold tabular-nums">{formatMoney(net, currency)}</dd>
              </div>
              {needsRate && (
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-text-2">В сумах по Курсу</dt>
                  <dd className="m-0 font-semibold tabular-nums">{formatMoney(amountUzs, 'UZS')}</dd>
                </div>
              )}
            </dl>
          )}

          {formData.userId ? (
            <div className="flex flex-col gap-1.5">
              <span className="text-[13px] font-semibold leading-tight">Покупатель</span>
              <div className="flex items-center justify-between gap-2 rounded-xl border border-border-soft bg-muted py-1 pl-3 pr-1">
                <span className="truncate text-sm">
                  {selectedUser
                    ? `${selectedUser.username}${selectedUser.email ? ` · ${selectedUser.email}` : ''}`
                    : formData.userId}
                </span>
                <Button variant="ghost" size="icon-sm" onClick={clearUser} aria-label="Убрать покупателя">
                  <X className="text-text-2" />
                </Button>
              </div>
            </div>
          ) : (
            <FormField
              label="Покупатель"
              hint={isSearching ? 'Ищем…' : 'Необязательно. Минимум 3 символа'}
            >
              <Input
                type="text"
                placeholder="Ник, email или id пользователя"
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
              />
            </FormField>
          )}
          {!formData.userId && results.length > 0 && (
            <ul aria-label="Найденные пользователи" className="m-0 -mt-2 list-none overflow-hidden rounded-xl border border-border p-0">
              {results.map((user) => (
                <li key={user.id} className="border-t border-border-soft first:border-t-0">
                  <button
                    type="button"
                    onClick={() => pickUser(user)}
                    className="w-full px-3 py-2 text-left text-sm outline-none transition-colors hover:bg-surface-alt focus-visible:bg-surface-alt"
                  >
                    {user.username}
                    {user.email ? ` · ${user.email}` : ''}
                    {user.isSubscribed ? ' · премиум' : ''}
                  </button>
                </li>
              ))}
            </ul>
          )}

          <FormField label="Комментарий">
            <Input
              type="text"
              placeholder="Например: iap_halfyearly_6, отчёт RevenueCat"
              value={formData.comment || ''}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, comment: e.target.value }))}
            />
          </FormField>
        </DialogBody>

        <DialogFooter bordered>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button onClick={onSubmit} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="animate-spin" />}
            {isEditing ? 'Сохранить' : 'Добавить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
